import { createContext, useCallback, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import type { JSX, ReactNode } from 'react';

import { getFragmentData } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';

import { LoadingFailure } from '../../components/LoadingFailure';
import { getLoadingFailureMessage } from '../../utils/loading-failure-message.util';
import { useFindOneQuotationQuery } from '../../pages/MissionQuotation/hooks/useFindOneQuotationQuery.hook';
import { MissionQuotationPage_QuotationInfo_Fragment } from '../../pages/MissionQuotation/fragments/quotation-info.fragment';
import { useMissionContext } from '../MissionContext/hooks/useMissionContext.hook';
import { MissionContextStatus } from '../MissionContext/types/mission-context.type';

import { QuotationContextStatus } from './types/quotation-context.type';
import type { QuotationContextData } from './types/quotation-context.type';

const QuotationContext = createContext<QuotationContextData | null>(null);

interface Props {
  readonly children?: ReactNode;
}

const QuotationContextProvider = ({
  children,
}: Readonly<Props>): JSX.Element => {
  const { status: missionStatus, mission } = useMissionContext();

  const missionQuotationInfo = getFragmentData(
    MissionQuotationPage_QuotationInfo_Fragment,
    mission?.quotationInfo,
  );

  const missionQuotationId = missionQuotationInfo?.quotationId ?? EMPTY_ID;

  const {
    data: quotationData,
    isLoading: isQuotationLoading,
    isError: isQuotationError,
    error: quotationError,
    refetch: refetchQuotation,
  } = useFindOneQuotationQuery(missionQuotationId);
  const missionQuotation = quotationData?.findOneQuotation;

  const retry = useCallback(() => {
    void refetchQuotation();
  }, [refetchQuotation]);

  const failureMessage = getLoadingFailureMessage({
    isLoading: isQuotationLoading,
    isError: isQuotationError,
    error: quotationError,
    isFound: !missionQuotationInfo?.quotationId || !!missionQuotation,
    subject: 'le devis',
    notFoundMessage: 'Ce devis est introuvable.',
  });

  const contextValue = useMemo<QuotationContextData>(() => {
    // If mission is still loading, quotation context is also loading
    if (missionStatus === MissionContextStatus.Loading || isQuotationLoading) {
      return {
        status: QuotationContextStatus.Loading,
        missionQuotationId: undefined,
        missionQuotation: undefined,
        missionQuotationInfo: undefined,
      };
    }

    // If mission has error, quotation context also has error
    if (missionStatus === MissionContextStatus.Error || isQuotationError) {
      return {
        status: QuotationContextStatus.Error,
        missionQuotationId: undefined,
        missionQuotation: undefined,
        missionQuotationInfo: undefined,
      };
    }

    // If no quotation ID, quotation is not initialized
    if (!missionQuotationInfo?.quotationId) {
      return {
        status: QuotationContextStatus.NotInitialized,
        missionQuotationId: undefined,
        missionQuotation: undefined,
        missionQuotationInfo: undefined,
      };
    }

    // The mission references a quotation that could not be found
    if (!missionQuotation) {
      return {
        status: QuotationContextStatus.Error,
        missionQuotationId: undefined,
        missionQuotation: undefined,
        missionQuotationInfo: undefined,
      };
    }

    return {
      status: QuotationContextStatus.Ready,
      missionQuotationId: missionQuotationInfo.quotationId,
      missionQuotation,
      missionQuotationInfo,
    };
  }, [
    missionStatus,
    isQuotationLoading,
    isQuotationError,
    missionQuotation,
    missionQuotationInfo,
  ]);

  return (
    <QuotationContext.Provider value={contextValue}>
      {failureMessage ? (
        <LoadingFailure failure={failureMessage} onRetry={retry} />
      ) : (
        (children ?? <Outlet />)
      )}
    </QuotationContext.Provider>
  );
};

export { QuotationContext, QuotationContextProvider };
